/*!
 * GentlePup theme — header.js
 * Copyright © 2026 Murilo Scigliano. All rights reserved.
 * Proprietary. See LICENSE. dev@murilo.design
 */
if (!customElements.get("gp-header")) {
  class GentlePupHeader extends HTMLElement {
    connectedCallback() {
      if (this.abortController) return;

      this.abortController = new AbortController();

      /* Tuck on scroll down, return on scroll up. The wrapper is the
         sticky element (see shell.css), so the class lands there. The
         dead zone near the top keeps the header planted while the
         announcement bar is still in view, and a threshold keeps a
         trembling trackpad from flickering it. */
      if (this.classList.contains("site-header--sticky")) {
        const wrapper = this.closest(".shopify-section") || this;
        let last = window.scrollY;
        let headerHeight = this.offsetHeight;
        const cacheHeight = () => {
          headerHeight = this.offsetHeight;
        };
        window.addEventListener("resize", cacheHeight, {
          signal: this.abortController.signal,
        });
        window.addEventListener(
          "scroll",
          () => {
            if (this.dialog?.open) return;
            if (this.panel && !this.panel.hidden) return;
            const y = window.scrollY;
            const delta = y - last;
            last = y;
            if (y < headerHeight * 2) wrapper.classList.remove("site-header-tucked");
            else if (delta > 8) wrapper.classList.add("site-header-tucked");
            else if (delta < -8) wrapper.classList.remove("site-header-tucked");
          },
          { passive: true, signal: this.abortController.signal },
        );
      }

      this.dialog = this.querySelector("[data-mobile-menu]");
      this.openButton = this.querySelector("[data-mobile-open]");
      this.closeButton = this.querySelector("[data-mobile-close]");
      // Must match the CSS breakpoint in shell.css. The nav moved to
      // 1100px — five long labels need about 1035px before the row is
      // comfortable — and a mismatch here leaves the burger opening a
      // dialog that never closes on resize between the two values.
      this.desktopMedia = window.matchMedia("(min-width: 1160px)");

      const options = { signal: this.abortController.signal };

      /* The shop panel. A click toggles it; Escape, a click outside,
         focus leaving it, or the desktop breakpoint going away close it.
         No hover: a panel that opens on hover is a panel keyboard and
         touch users never see. */
      this.panel = this.querySelector("[data-panel]");
      this.panelToggle = this.querySelector("[data-panel-toggle]");
      this.panelScrim = this.querySelector("[data-panel-scrim]");
      if (this.panel && this.panelToggle) {
        this.panelToggle.addEventListener("click", () => this.togglePanel(), options);
        this.panelScrim?.addEventListener("click", () => this.closePanel(), options);

        /* Hover intent on a fine pointer, as an addition to the click:
           a mouse user expects the panel to open as they arrive and to
           stay while they cross into it. Short delays keep a pass-through
           from flashing it. Touch and keyboard never enter here. */
        if (window.matchMedia("(hover: hover) and (pointer: fine)").matches) {
          const item = this.panelToggle.closest("li") || this.panelToggle;
          const cancel = () => { clearTimeout(this.hoverTimer); this.hoverTimer = null; };
          const arm = (fn, ms) => { cancel(); this.hoverTimer = setTimeout(fn, ms); };
          item.addEventListener("mouseenter", () => arm(() => this.openPanel(), 90), options);
          item.addEventListener("mouseleave", () => arm(() => this.closePanel(), 260), options);
          this.panel.addEventListener("mouseenter", cancel, options);
          this.panel.addEventListener("mouseleave", () => arm(() => this.closePanel(), 260), options);
        }

        /* Arrow keys travel the cards; Home and End jump. */
        this.panel.addEventListener(
          "keydown",
          (event) => {
            const cards = Array.from(this.panel.querySelectorAll(".shop-panel__card, .shop-panel__all"));
            const at = cards.indexOf(document.activeElement);
            if (at < 0) return;
            const step = { ArrowRight: 1, ArrowDown: 1, ArrowLeft: -1, ArrowUp: -1 }[event.key];
            if (step) {
              event.preventDefault();
              cards[(at + step + cards.length) % cards.length].focus();
            } else if (event.key === "Home") { event.preventDefault(); cards[0].focus(); }
            else if (event.key === "End") { event.preventDefault(); cards[cards.length - 1].focus(); }
          },
          options,
        );
        document.addEventListener(
          "keydown",
          (event) => {
            if (event.key === "Escape" && !this.panel.hidden) {
              this.closePanel();
              this.panelToggle.focus();
            }
          },
          options,
        );
        document.addEventListener(
          "click",
          (event) => {
            if (this.panel.hidden) return;
            if (this.panel.contains(event.target) || this.panelToggle.contains(event.target)) return;
            this.closePanel();
          },
          options,
        );
        this.panel.addEventListener(
          "focusout",
          (event) => {
            const next = event.relatedTarget;
            if (next && (this.panel.contains(next) || this.panelToggle.contains(next))) return;
            if (next) this.closePanel();
          },
          options,
        );
        this.desktopMedia.addEventListener("change", (event) => { if (!event.matches) this.closePanel(); }, options);
      }

      if (!this.dialog || !this.openButton || !this.closeButton) return;

      this.openButton.addEventListener("click", (event) => this.openMenu(event), options);
      this.closeButton.addEventListener("click", () => this.closeMenu(), options);
      this.dialog.addEventListener("click", (event) => this.handleDialogClick(event), options);
      this.dialog.addEventListener("close", () => this.handleClose(), options);
      this.dialog.addEventListener("cancel", () => this.unlockPage(), options);
      this.desktopMedia.addEventListener(
        "change",
        (event) => {
          if (event.matches) this.closeMenu();
        },
        options,
      );
    }

    disconnectedCallback() {
      clearTimeout(this.closeTimer);
      clearTimeout(this.hoverTimer);
      clearTimeout(this.panelCloseTimer);
      this.closePanel();
      this.closing = false;
      if (this.dialog?.open) this.dialog.close();
      this.unlockPage();
      this.abortController?.abort();
      this.abortController = null;
    }

    togglePanel() {
      if (this.panel.hidden) this.openPanel();
      else this.closePanel();
    }

    openPanel() {
      if (!this.panel.hidden && this.panel.hasAttribute("data-open")) return;
      clearTimeout(this.panelCloseTimer);
      this.panel.hidden = false;
      if (this.panelScrim) this.panelScrim.hidden = false;
      this.panelToggle.setAttribute("aria-expanded", "true");
      // Un-hidden this frame, opened the next: the transition needs a
      // painted starting state, or the panel and its cards simply appear.
      requestAnimationFrame(() => requestAnimationFrame(() => this.panel.setAttribute("data-open", "")));
    }

    closePanel() {
      if (!this.panel || this.panel.hidden) return;
      this.panel.removeAttribute("data-open");
      if (this.panelScrim) this.panelScrim.hidden = true;
      this.panelToggle.setAttribute("aria-expanded", "false");
      clearTimeout(this.panelCloseTimer);
      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      // Matches the exit: --duration-fast on the contents, --duration on
      // the curtain. 320ms held the panel in the DOM past its own paint.
      this.panelCloseTimer = setTimeout(() => { this.panel.hidden = true; }, reduced ? 0 : 280);
    }

    openMenu(event) {
      if (!this.dialog || this.dialog.open) return;

      this.activeElement = event.currentTarget;
      this.dialog.showModal();
      this.openButton.setAttribute("aria-expanded", "true");
      document.documentElement.style.overflow = "hidden";
      this.closeButton.focus();
    }

    /**
     * Closes the drawer, letting the exit animation finish first.
     *
     * Same shape as gp-cart's close: a native dialog vanishes the instant
     * close() is called, so data-closing gives the slide-out somewhere to
     * run. The timeout is the safety net — animationend does not fire if
     * the tab is hidden mid-close, and a drawer stuck open traps focus.
     */
    closeMenu() {
      if (!this.dialog?.open || this.closing) return;

      const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
      if (reduced) {
        this.dialog.close();
        return;
      }

      this.closing = true;
      this.dialog.setAttribute("data-closing", "");

      const finish = () => {
        clearTimeout(this.closeTimer);
        this.dialog.removeEventListener("animationend", finish);
        this.dialog.removeAttribute("data-closing");
        this.closing = false;
        if (this.dialog.open) this.dialog.close();
      };

      this.dialog.addEventListener("animationend", finish, { once: true });
      this.closeTimer = setTimeout(finish, 500);
    }

    handleDialogClick(event) {
      if (event.target === this.dialog) this.closeMenu();
    }

    handleClose() {
      this.unlockPage();
      this.openButton?.setAttribute("aria-expanded", "false");
      this.activeElement?.focus();
      this.activeElement = null;
    }

    unlockPage() {
      document.documentElement.style.removeProperty("overflow");
    }
  }

  customElements.define("gp-header", GentlePupHeader);
}
